/**
 * Demo Prompts Component
 * Development-only quick-select prompts for testing MCP capabilities
 * Uses actual user data for realistic testing
 */

import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Copy, Zap, RefreshCw } from 'lucide-react';

export interface DemoPrompt {
  id: string;
  category: string;
  label: string;
  prompt: string;
  expectedIntent: string;
  description: string;
  requiresData?: 'project' | 'task' | 'staff' | 'client' | 'timesheet';
}

interface UserData {
  projects: Array<{ _id: string; name: string; code?: string }>;
  tasks: Array<{ _id: string; title: string; description?: string }>;
  staff: Array<{ _id: string; firstName: string; lastName: string; email?: string }>;
  clients: Array<{ _id: string; name: string; email?: string }>;
  users: Array<{ _id: string; firstName: string; lastName: string; email: string }>;
  hasTimesheets: boolean;
}

// Base prompt templates (will be populated with real data)
export const DEMO_PROMPT_TEMPLATES: Omit<DemoPrompt, 'prompt'>[] = [
  // CREATION
  {
    id: 'create_timesheet_1',
    category: 'Creation',
    label: 'Create Timesheet (Basic)',
    expectedIntent: 'create_timesheet',
    description: 'Basic timesheet creation with explicit parameters',
    requiresData: 'project'
  },
  {
    id: 'create_timesheet_2',
    category: 'Creation',
    label: 'Create Timesheet (Natural)',
    expectedIntent: 'create_timesheet',
    description: 'Natural language timesheet creation',
    requiresData: 'project'
  },
  {
    id: 'create_project',
    category: 'Creation',
    label: 'Create Project',
    expectedIntent: 'create_project',
    description: 'Project creation with multiple parameters',
    requiresData: 'client'
  },
  {
    id: 'create_task',
    category: 'Creation',
    label: 'Create Task',
    expectedIntent: 'create_task',
    description: 'Task creation with project context',
    requiresData: 'project'
  },

  // MEMORY USAGE
  {
    id: 'memory_yesterday',
    category: 'Memory',
    label: 'Reference Yesterday',
    expectedIntent: 'create_timesheet',
    description: 'Uses recentManualActions from LTM',
    requiresData: 'timesheet'
  },
  {
    id: 'memory_last_week',
    category: 'Memory',
    label: 'Reference Last Week',
    expectedIntent: 'create_timesheet',
    description: 'Temporal reference with LTM lookup',
    requiresData: 'timesheet'
  },
  {
    id: 'memory_frequent_project',
    category: 'Memory',
    label: 'Frequent Project',
    expectedIntent: 'create_timesheet',
    description: 'Uses frequentEntities from LTM'
  },
  {
    id: 'memory_default_project',
    category: 'Memory',
    label: 'Default Project',
    expectedIntent: 'create_timesheet',
    description: 'Uses default project from user preferences'
  },

  // RETRIEVAL / QUERIES
  {
    id: 'query_project_summary',
    category: 'Retrieval',
    label: 'Project Summary',
    expectedIntent: 'get_project_summary',
    description: 'Query project information',
    requiresData: 'project'
  },
  {
    id: 'query_timesheet_summary',
    category: 'Retrieval',
    label: 'Timesheet Summary',
    expectedIntent: 'get_timesheet_summary',
    description: 'Query timesheet data with date range'
  },
  {
    id: 'query_task_summary',
    category: 'Retrieval',
    label: 'Task Summary',
    expectedIntent: 'get_task_summary',
    description: 'Query tasks with filters',
    requiresData: 'project'
  },
  {
    id: 'query_general',
    category: 'Retrieval',
    label: 'General Query',
    expectedIntent: 'query_data',
    description: 'General data query with aggregation'
  },

  // EDITING
  {
    id: 'edit_timesheet',
    category: 'Editing',
    label: 'Edit Timesheet',
    expectedIntent: 'edit_timesheet',
    description: 'Edit existing timesheet with memory reference',
    requiresData: 'timesheet'
  },
  {
    id: 'edit_project',
    category: 'Editing',
    label: 'Edit Project',
    expectedIntent: 'edit_project',
    description: 'Edit project details',
    requiresData: 'project'
  },
  {
    id: 'edit_task',
    category: 'Editing',
    label: 'Edit Task',
    expectedIntent: 'edit_task',
    description: 'Edit task attributes',
    requiresData: 'task'
  },

  // DELETION
  {
    id: 'delete_timesheet',
    category: 'Deletion',
    label: 'Delete Timesheet',
    expectedIntent: 'delete_timesheet',
    description: 'Delete with temporal reference',
    requiresData: 'timesheet'
  },
  {
    id: 'delete_task',
    category: 'Deletion',
    label: 'Delete Task',
    expectedIntent: 'delete_task',
    description: 'Delete task with context',
    requiresData: 'task'
  },

  // APPROVAL
  {
    id: 'approve_timesheet',
    category: 'Approval',
    label: 'Approve Timesheet',
    expectedIntent: 'approve_timesheet',
    description: 'Tests permission checking',
    requiresData: 'staff'
  },

  // MESSAGING
  {
    id: 'send_sms',
    category: 'Messaging',
    label: 'Send SMS',
    expectedIntent: 'send_sms',
    description: 'SMS messaging with recipient resolution',
    requiresData: 'staff'
  },
  {
    id: 'send_email',
    category: 'Messaging',
    label: 'Send Email',
    expectedIntent: 'send_email',
    description: 'Email with context-based recipient',
    requiresData: 'client'
  },

  // REPORTS
  {
    id: 'generate_report',
    category: 'Reports',
    label: 'Generate Report',
    expectedIntent: 'generate_report',
    description: 'Report generation with filters',
    requiresData: 'project'
  },

  // CONVERSATIONAL
  {
    id: 'general_help',
    category: 'Conversational',
    label: 'Ask for Help',
    expectedIntent: 'general_chat',
    description: 'General capabilities inquiry'
  },
  {
    id: 'general_greeting',
    category: 'Conversational',
    label: 'Greeting',
    expectedIntent: 'general_chat',
    description: 'Friendly conversation'
  },

  // COMPLEX SCENARIOS
  {
    id: 'complex_multi_step',
    category: 'Complex',
    label: 'Multi-Step Request',
    expectedIntent: 'create_timesheet',
    description: 'Tests handling multiple intents (should handle first, then ask about second)',
    requiresData: 'project'
  },
  {
    id: 'complex_ambiguous',
    category: 'Complex',
    label: 'Ambiguous Reference',
    expectedIntent: 'create_timesheet',
    description: 'Tests memory and context resolution',
    requiresData: 'project'
  },
  
  // CROSS-USER QUERIES (Entity Resolution)
  {
    id: 'cross_user_tasks',
    category: 'Cross-User',
    label: 'User Task Query',
    expectedIntent: 'get_task_summary',
    description: 'Query tasks assigned to another user by name',
    requiresData: 'staff'
  },
  {
    id: 'cross_user_timesheet',
    category: 'Cross-User',
    label: 'User Timesheet Query',
    expectedIntent: 'get_timesheet_summary',
    description: 'Query timesheets for another user',
    requiresData: 'staff'
  },
  {
    id: 'cross_user_workload',
    category: 'Cross-User',
    label: 'User Workload',
    expectedIntent: 'query_data',
    description: 'Query workload/hours for another user',
    requiresData: 'staff'
  },
  {
    id: 'cross_user_approve',
    category: 'Cross-User',
    label: 'Approve Specific User',
    expectedIntent: 'approve_timesheet',
    description: 'Approve specific user by name',
    requiresData: 'staff'
  },
  {
    id: 'cross_user_message',
    category: 'Cross-User',
    label: 'Message Team Member',
    expectedIntent: 'send_sms',
    description: 'Send message to specific team member',
    requiresData: 'staff'
  },
];

// Generate actual prompts based on user data
function generatePromptText(template: Omit<DemoPrompt, 'prompt'>, userData: UserData): string {
  // Use real data with descriptive fallbacks
  const project = userData.projects[0]?.name || 'current project';
  const project2 = userData.projects[1]?.name || 'second project';
  const projectCode = userData.projects[0]?.code || 'PROJ';
  
  const task = userData.tasks[0]?.title || 'pending task';
  const task2 = userData.tasks[1]?.title || 'design task';
  
  // Staff names for cross-user queries (prefer staff, fallback to users)
  const staffOrUsers = userData.staff.length > 0 ? userData.staff : userData.users;
  const staff1 = staffOrUsers[0] ? `${staffOrUsers[0].firstName}` : 'team member';
  const staff2 = staffOrUsers[1] ? `${staffOrUsers[1].firstName}` : 'colleague';
  const staff1Full = staffOrUsers[0] ? `${staffOrUsers[0].firstName} ${staffOrUsers[0].lastName}` : 'team member';
  const staff2Full = staffOrUsers[1] ? `${staffOrUsers[1].firstName} ${staffOrUsers[1].lastName}` : 'colleague';
  
  const client = userData.clients[0]?.name || 'client';
  const client2 = userData.clients[1]?.name || 'second client';

  const promptMap: Record<string, string> = {
    // CREATION - Use real project/task names
    'create_timesheet_1': `Create a timesheet for ${project} on Monday with 8 hours`,
    'create_timesheet_2': `I worked on ${project} for 6.5 hours yesterday`,
    'create_project': `Create a new project called "Mobile App Development" for client ${client} with a budget of $50,000`,
    'create_task': `Create a task for designing the login page with high priority for ${project}`,
    
    // MEMORY - Pure memory references
    'memory_yesterday': 'Create a timesheet the same as yesterday',
    'memory_last_week': 'Log the same hours I worked last Monday',
    'memory_frequent_project': 'Log 7 hours on my usual project',
    'memory_default_project': 'Add a timesheet for today with 8 hours',
    
    // RETRIEVAL - Use real data
    'query_project_summary': `What's the status of ${project}?`,
    'query_timesheet_summary': 'Show me my timesheets for this week',
    'query_task_summary': `What tasks are assigned to me for ${project}?`,
    'query_general': 'How many hours did I work last week?',
    
    // EDITING - Use real task and project names
    'edit_timesheet': "Change yesterday's timesheet hours from 8 to 7.5",
    'edit_project': `Update ${project} budget to $75,000`,
    'edit_task': `Change the priority of ${task} to medium`,
    
    // DELETION - Use real task names
    'delete_timesheet': 'Delete my timesheet from last Friday',
    'delete_task': `Remove ${task2} from the project`,
    
    // APPROVAL - Use real staff name
    'approve_timesheet': `Approve ${staff1Full}'s timesheet for this week`,
    
    // MESSAGING - Use real staff and client names
    'send_sms': `Send an SMS to ${staff1} saying the materials have arrived`,
    'send_email': `Email ${client} a project status update`,
    
    // REPORTS - Use real project name
    'generate_report': `Generate a PDF report of all timesheets for ${project} this month`,
    
    // CONVERSATIONAL
    'general_help': 'What can you help me with?',
    'general_greeting': 'Hello! How are you today?',
    
    // COMPLEX - Use real data
    'complex_multi_step': `Create a timesheet for ${project} with 8 hours today, then send an email to the project manager`,
    'complex_ambiguous': 'Log my hours on that project we discussed',
    
    // CROSS-USER QUERIES - Tests entity resolution with real user names
    // These test the AI's ability to:
    // 1. Recognize a person's name
    // 2. Look up that user in the system
    // 3. Query or modify data related to that user
    // AI processes this request step-by-step
    'cross_user_tasks': `What is ${staff1} assigned to today?`,
    'cross_user_timesheet': `Show me ${staff1Full}'s timesheets for this week`,
    'cross_user_workload': `How many hours did ${staff1} work on ${project} last week?`,
    'cross_user_approve': `Approve ${staff2Full}'s timesheet from yesterday`,
    'cross_user_message': `Send a message to ${staff1} asking about the ${task} progress`,
  };

  return promptMap[template.id] || 'Test prompt';
}

interface DemoPromptsProps {
  onSelectPrompt: (prompt: string) => void;
  onCollapse?: () => void;
}

export const DemoPrompts: React.FC<DemoPromptsProps> = ({ onSelectPrompt, onCollapse }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [userData, setUserData] = useState<UserData>({
    projects: [],
    tasks: [],
    staff: [],
    clients: [],
    users: [],
    hasTimesheets: false
  });
  const [loading, setLoading] = useState(false);
  const [prompts, setPrompts] = useState<DemoPrompt[]>([]);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Fetch user data
  useEffect(() => {
    if (isExpanded && userData.projects.length === 0) {
      fetchUserData();
    }
  }, [isExpanded]);

  // Generate prompts when user data changes
  useEffect(() => {
    const generatedPrompts = DEMO_PROMPT_TEMPLATES.map(template => ({
      ...template,
      prompt: generatePromptText(template, userData)
    }));
    setPrompts(generatedPrompts);
  }, [userData]);

  const fetchUserData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Fetch projects
      const projectsRes = await fetch(`${apiUrl}/api/projects?limit=5`, { headers });
      const projectsData = await projectsRes.json();
      const projects = projectsData.success ? projectsData.data.slice(0, 5) : [];

      // Fetch tasks
      const tasksRes = await fetch(`${apiUrl}/api/tasks?limit=5`, { headers });
      const tasksData = await tasksRes.json();
      const tasks = tasksData.success ? tasksData.data.slice(0, 5) : [];

      // Fetch staff
      const staffRes = await fetch(`${apiUrl}/api/staff?limit=5`, { headers });
      const staffData = await staffRes.json();
      const staff = staffData.success ? staffData.data.slice(0, 5) : [];

      // Fetch clients (contacts with isClient=true)
      const clientsRes = await fetch(`${apiUrl}/api/contacts?isClient=true&limit=5`, { headers });
      const clientsData = await clientsRes.json();
      const clients = clientsData.success ? clientsData.data.slice(0, 5) : [];

      // Fetch users from account (for cross-user queries)
      const usersRes = await fetch(`${apiUrl}/api/users/account-users`, { headers });
      const usersData = await usersRes.json();
      const users = usersData.success ? usersData.data.slice(0, 5) : [];

      // Check for timesheets
      const timesheetsRes = await fetch(`${apiUrl}/api/timesheets?limit=1`, { headers });
      const timesheetsData = await timesheetsRes.json();
      const hasTimesheets = timesheetsData.success && timesheetsData.data.length > 0;

      setUserData({
        projects,
        tasks,
        staff,
        clients,
        users,
        hasTimesheets
      });
    } catch (error) {
      console.error('[DemoPrompts] Failed to fetch user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['All', ...Array.from(new Set(DEMO_PROMPT_TEMPLATES.map(p => p.category)))];
  
  const filteredPrompts = selectedCategory === 'All' 
    ? prompts 
    : prompts.filter(p => p.category === selectedCategory);

  const handleCopy = (prompt: string) => {
    navigator.clipboard.writeText(prompt);
  };

  const hasNoData = userData.projects.length === 0 && userData.tasks.length === 0;

  return (
    <div className="border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-2 flex items-center justify-between hover:bg-purple-100/50 dark:hover:bg-purple-900/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-purple-600 dark:text-purple-400" />
          <span className="text-sm font-semibold text-purple-900 dark:text-purple-200">
            Demo Prompts ({prompts.length})
          </span>
          <span className="text-xs px-2 py-0.5 bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-200 rounded">
            DEV MODE
          </span>
          {loading && <RefreshCw className="w-3 h-3 text-purple-600 dark:text-purple-400 animate-spin" />}
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        )}
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-3">
          {/* Data Status */}
          {hasNoData && (
            <div className="bg-yellow-50 dark:bg-gray-700 dark:border dark:border-warning-800/50 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 text-xs">
              <p className="text-warning-800 dark:text-warning-200 font-semibold mb-1">
                ⚠️ Limited Data Available
              </p>
              <p className="text-warning-700 dark:text-warning-300">
                Some prompts use placeholder data because you don't have projects or tasks yet. 
                Create some projects to see fully personalized prompts!
              </p>
            </div>
          )}

          {/* Data Summary */}
          {!hasNoData && (
            <div className="space-y-2">
              <div className="flex gap-2 text-xs text-gray-600 dark:text-gray-400">
                <span>📊 Using your real data:</span>
                <span className="font-semibold">{userData.projects.length} projects</span>
                <span>•</span>
                <span className="font-semibold">{userData.tasks.length} tasks</span>
                <span>•</span>
                <span className="font-semibold">{(userData.staff.length + userData.users.length)} team members</span>
                <button 
                  onClick={fetchUserData}
                  className="ml-auto text-purple-600 dark:text-purple-400 hover:underline flex items-center gap-1"
                  disabled={loading}
                >
                  <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>
              {(userData.staff.length > 0 || userData.users.length > 0) && (
                <div className="text-xs text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-gray-700 dark:border dark:border-purple-800/50 px-2 py-1 rounded">
                  💡 <strong>Cross-User Prompts:</strong> Using names like{' '}
                  <span className="font-semibold">
                    {userData.staff[0]?.firstName || userData.users[0]?.firstName}
                  </span>
                  {' '}to test entity resolution!
                </div>
              )}
            </div>
          )}

          {/* Category Filter */}
          <div className="flex gap-2 flex-wrap">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  selectedCategory === cat
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Prompts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-96 overflow-y-auto">
            {filteredPrompts.map(prompt => (
              <div
                key={prompt.id}
                className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-600 hover:border-purple-400 dark:hover:border-purple-500 transition-colors group"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {prompt.label}
                      </h4>
                      <span className="text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                        {prompt.category}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      {prompt.description}
                    </p>
                    <p className="text-xs text-purple-600 dark:text-purple-400 font-mono bg-purple-50 dark:bg-gray-700 dark:border dark:border-purple-800/50 px-2 py-1 rounded">
                      Expected: {prompt.expectedIntent}
                    </p>
                  </div>
                </div>
                
                <div className="bg-gray-50 dark:bg-gray-700 p-2 rounded text-xs text-gray-700 dark:text-gray-300 mb-2 border border-gray-200 dark:border-gray-600">
                  "{prompt.prompt}"
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      onSelectPrompt(prompt.prompt);
                      setIsExpanded(false); // Collapse after selecting
                    }}
                    className="flex-1 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded text-xs font-medium transition-colors flex items-center justify-center gap-1"
                  >
                    <Zap className="w-3 h-3" />
                    Send Now
                  </button>
                  <button
                    onClick={() => handleCopy(prompt.prompt)}
                    className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-300 rounded text-xs transition-colors"
                    title="Copy to clipboard"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Info Footer */}
          <div className="text-xs text-gray-600 dark:text-gray-400 bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50 p-2 rounded">
            💡 <strong>Tip:</strong> Prompts use your actual projects, tasks, and staff! Click "Test This" to auto-fill, or use the Brain icon (🧠) to compare implementations.
          </div>
        </div>
      )}
    </div>
  );
};

